import { useThree } from '@react-three/fiber';
import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { useGetPanelReplacementGrid } from 'api/queries/articles/panelReplacementGrid';
import { useReplacementLifecycleHandler } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/hooks/useReplacementLifecycleHandler';
import { useReplacementOpacityHandler } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/hooks/useReplacementOpacityHandler';
import { useReplacementPointerEventHandler } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/hooks/useReplacementPointerEventHandler';
import { useReplacementScreenDirectionsHandler } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/hooks/useReplacementScreenDirectionsHandler';
import { useReplacementStateHandler } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/hooks/useReplacementStateHandler';
import { QuickPanelReplacementControls } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/QuickPanelReplacementControls';
import {
    EMPTY_PANEL_REPLACEMENT_GRID,
    buildEdgeHandles,
    canContinuePanelQuickReplacement,
    getCachedPanelReplacementGrid,
    getCompatiblePanels,
    getPanelNormalOffset,
    getPanelReplacementGrid,
    getSelectedPanel,
    isRotation90Degrees
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const systemId = import.meta.env.VITE_DEFAULT_SYSTEM_ID;

export const QuickPanelReplacement = () => {
    const filterActive = usePlacementStore((state) => state.isPPMFilterActive);
    const isOpen = usePlacementStore((state) => state.isPanelQuickReplacementOpen);
    const isViewMode = usePlacementStore((state) => state.isViewMode);
    const selectionCount = usePlacementStore((state) => Object.keys(state.articleInstanceSelections).length);

    const camera = useThree((state) => state.camera);
    const renderer = useThree((state) => state.gl);
    const invalidate = useThree((state) => state.invalidate);
    const scene = useThree((state) => state.scene);

    const {
        data: panelResponse,
        isError: isPanelQueryError,
        isLoading: isPanelQueryLoading
    } = useGetPanelReplacementGrid(
        { systemId, filterActive },
        { query: { enabled: isOpen && Boolean(systemId), staleTime: 24 * 60 * 60 * 1000 } }
    );

    const grid = useMemo(
        () =>
            panelResponse?.data
                ? getPanelReplacementGrid(systemId, filterActive, panelResponse.data)
                : (getCachedPanelReplacementGrid(systemId, filterActive) ?? EMPTY_PANEL_REPLACEMENT_GRID),
        [filterActive, panelResponse]
    );

    const selected = usePlacementStore(useShallow((state) => getSelectedPanel(state, grid)));
    const {
        activeEdge,
        canvasRef,
        handleEdgeClick,
        initialCursorRef,
        mouseEdgeRef,
        mousePreview,
        previewRef,
        setActiveEdge,
        setMousePreview
    } = useReplacementStateHandler();

    const anchorPart = selected ? selected.instance.parts[Object.keys(selected.instance.parts)[0]] : undefined;
    const panelNormalOffset = usePlacementStore((state) =>
        selected
            ? getPanelNormalOffset(
                  state,
                  selected.articleId,
                  Object.keys(selected.instance.parts)[0],
                  selected.item.bounds
              )
            : 0
    );

    const selectionToken = selected ? `${selected.articleId}:${selected.instanceId}` : '';
    const edgeHandles = useMemo(
        () => (selected && anchorPart ? buildEdgeHandles(selected.item, anchorPart, panelNormalOffset) : []),
        [anchorPart, panelNormalOffset, selected]
    );

    const { currentScreenDirections, viewRevision } = useReplacementScreenDirectionsHandler(
        isOpen,
        camera,
        edgeHandles,
        activeEdge
    );

    const snapCandidates = useMemo(
        () =>
            activeEdge && selected && anchorPart
                ? getCompatiblePanels(
                      grid,
                      selected.item,
                      activeEdge.dimension,
                      isRotation90Degrees(anchorPart.rotation),
                      Object.keys(selected.instance.parts).length
                  )
                : [],
        [activeEdge, anchorPart, grid, selected]
    );

    useReplacementLifecycleHandler({
        activeEdge,
        camera,
        canvasRef,
        grid,
        initialCursorRef,
        invalidate,
        isOpen,
        isQueryError: isPanelQueryError,
        isQueryLoading: isPanelQueryLoading,
        isViewMode,
        mouseEdgeRef,
        mousePreview,
        previewRef,
        selectionCount,
        setActiveEdge,
        setMousePreview,
        viewRevision
    });

    useReplacementOpacityHandler(activeEdge !== null, invalidate);

    useReplacementPointerEventHandler({
        anchorPart,
        camera,
        canvasRef,
        currentScreenDirections,
        edgeHandles,
        grid,
        initialCursorRef,
        isOpen,
        mouseEdgeRef,
        renderer,
        scene,
        selected,
        setActiveEdge,
        setMousePreview,
        snapCandidates,
        previewRef
    });

    if (!isOpen || !selected || !anchorPart || edgeHandles.length !== 4) {
        return null;
    }

    const controlsEnabled = canContinuePanelQuickReplacement(usePlacementStore.getState()) && !isPanelQueryLoading;
    return (
        <QuickPanelReplacementControls
            activeEdge={activeEdge}
            anchorPart={anchorPart}
            camera={camera}
            controlsEnabled={controlsEnabled}
            edgeHandles={edgeHandles}
            grid={grid}
            mousePreview={mousePreview}
            screenDirections={currentScreenDirections}
            selected={selected}
            selectionToken={selectionToken}
            snapCandidates={snapCandidates}
            onEdgeClick={handleEdgeClick}
        />
    );
};
